import { FC, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Button, Checkbox, Heading, NotificationBox, Stack, Stretch, Text } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { useFormContext } from 'react-hook-form';

import usePostalCodeBrusselsEligibilityCheck from '../../../hooks/usePostalCodeBrusselsEligibilityCheck';
import AddressAutocomplete from '../../../utils/addresses/AddressAutocomplete';
import { PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

export const PostalCodeStep: FC<FlowChildProps> = ({ nextMainStep }) => {
  const [showPostalCodeNotEligibleNotification, setShowPostalCodeNotEligibleNotification] = useState(false);

  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const {
    register,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { errors },
  } = useFormContext<PriceSimulationFormSchema>();
  const { trackEventBE } = useTracking();

  const postalCode = watch('postalCode');
  const customerType = watch('customerType');
  const cityWatch = watch('city');

  const {
    isLoadingPostalCodeEligible,
    isPostalCodeEligible,
    shouldShowError: shouldShowNotification,
  } = usePostalCodeBrusselsEligibilityCheck(postalCode, customerType || 'Residential');

  const onClickNext = async () => {
    const isValid = await trigger(['postalCode', 'city', 'isUserDomiciledOnPostalCode', 'isApiPostalCodeCityValid']);
    if (isPostalCodeEligible && isValid) {
      trackEventBE('pbt_calculation_zipcode', {
        status: 'click',
        data: getTrackableFormValues(getValues()),
      });
      nextMainStep();
    }
  };

  useEffect(() => {
    if (postalCode?.length === 0) {
      setShowPostalCodeNotEligibleNotification(false);
      return;
    }

    setShowPostalCodeNotEligibleNotification(shouldShowNotification);
  }, [postalCode, shouldShowNotification, customerType]);

  return (
    <>
      <Stack gap="2">
        <Heading as="h1" size="M">
          {fields.postalCodeSelectionStep.title.value}
        </Heading>
        <Text>{fields.postalCodeSelectionStep.subTitle.value}</Text>
      </Stack>

      <Stretch>
        <Stack>
          <AddressAutocomplete
            zipCodeFormField={{
              label: fields.postalCodeSelectionStep.postalCodeFormField.value.label,
              defaultValue: postalCode ?? '',
              yupProps: register('postalCode'),
            }}
            cityFormField={{
              defaultValue: cityWatch ?? '',
              yupProps: register('city'),
            }}
            onZipCodeCityChange={value => {
              setValue('city', value.city as string | undefined);
              setValue('postalCode', value.zipCode as string | undefined);
            }}
            isApiAddressValidYupProps={register('isApiPostalCodeCityValid')}
            zipCodeCityError={fields.postalCodeSelectionStep.postalCodeFormField.value.validationMessage}
            shouldValidateAddressInApi
          />
        </Stack>
      </Stretch>

      <Checkbox
        label={fields.postalCodeSelectionStep.termsAndConditionsCheckboxFormField.value.label}
        error={
          errors.isUserDomiciledOnPostalCode?.message
            ? fields.postalCodeSelectionStep.termsAndConditionsCheckboxFormField.value.validationMessage
            : ''
        }
        {...register('isUserDomiciledOnPostalCode')}
      />
      {showPostalCodeNotEligibleNotification && (
        <NotificationBox
          title={fields.postalCodeSelectionStep.postalCodeCheckNotification.value.title}
          text={<RichText html={fields.postalCodeSelectionStep.postalCodeCheckNotification.value.content} />}
          variant={fields.postalCodeSelectionStep.postalCodeCheckNotification.value.variant}
          isAlert={false}
        />
      )}

      <Stack gap="2" direction="row">
        <Button onClick={onClickNext} isLoading={isLoadingPostalCodeEligible}>
          {fields.navigation.nextButtonText.value}
        </Button>
      </Stack>

      <Stack direction="row">
        <Text color="textLowEmphasis" size="BodyS">
          <RichText html={fields.postalCodeSelectionStep.termsAndConditionsCheckboxLabel.value} />
        </Text>
      </Stack>
    </>
  );
};
